import React, { useState } from "react"
import { useParams } from "react-router-dom"

import Breadcrumb from "@/components/BreadcrumbNew"
import ActionDialog from "@/components/ActionDialog"
import ActionsDropdown from "@/pages/Admin/TrackableLinks/ActionsDropdown"
import MetricCards from "@/pages/Admin/TrackableLinks/TrackableLinkMetrics/MetricCards"
import TrackableLinkInteractions from "@/pages/Admin/TrackableLinks/TrackableLinkMetricsV2/TrackableLinkInteractions"
import TrackableLinkUTMMetrics from "@/pages/Admin/TrackableLinks/TrackableLinkMetrics/TrackableLinkUTMMetrics"
import TrackableLinkMetricsSkeleton from "@/pages/Admin/TrackableLinks/TrackableLinkMetrics/TrackableLinkMetricsSkeleton"
import SetupTrackableLinkAccordion from "@/pages/Admin/TrackableLinks/TrackableLinkMetrics/SetupTrackableLinkAccordion"

import { Button, Grid, MenuItem, Select, Tooltip, Typography } from "@material-ui/core"
import { Alert } from "@material-ui/lab"
import { DateTimePicker, MuiPickersUtilsProvider } from "@material-ui/pickers"
import DateFnsUtils from "@date-io/date-fns"
import { ptBR } from "date-fns/locale"
import { mountTrackableLinkURL } from "@/utils/trackableLinks"
import { Link as LinkIcon } from "@material-ui/icons"
import { Loading, Notification } from "@/components"

import { inboxAppUrl } from "@/config/url"
import {
	Trackablelink,
	TrackableLinkInteractionsHasRespondedFilter,
	TrackableLinkInteractionsPeriod,
	TrackableLinkInteractionsPeriodOption,
	TrackableLinkInteractionsSummary
} from "@/protocols/trackableLinks"
import ErrorHandlerService from "@/services/ErrorHandler"

import useDidMount from "@/hooks/useDidMount"
import useValidation, { ErrorType } from "@/hooks/useValidation"
import ApiService from "@/services/Api"
import copy from "copy-to-clipboard"
import { getDifferenceInDays } from "@/utils/time"
import {
	calculateDateRangeForPeriod,
	getCustomPeriodEndDateLimit,
	MAX_CUSTOM_RANGE_IN_DAYS
} from "@/utils/trackableLinkInteractionsPeriod"
import useStyles from "@/pages/Admin/TrackableLinks/TrackableLinkMetricsV2/styles"

type TrackableLinkResponseData = {
	trackablelink: Trackablelink
	percentageOfConversationsTracked: number
}

const DEFAULT_PERIOD_SUMMARY: TrackableLinkInteractionsSummary = {
	totalClicked: 0,
	totalResponded: 0
}

const DEFAULT_PERIOD: Exclude<TrackableLinkInteractionsPeriod, "custom"> = 7

const periodOptions: TrackableLinkInteractionsPeriodOption[] = [
	{ label: "Hoje", value: "today" },
	{ label: "Últimos 7 dias", value: 7 },
	{ label: "Personalizado", value: "custom" }
]

const TrackableLinkMetrics: React.FC = () => {
	const { trackableLinkId } = useParams<{ trackableLinkId?: string }>()
	const { triggerValidation } = useValidation()

	const { fromDate: defaultFromDate, toDate: defaultToDate } = calculateDateRangeForPeriod(DEFAULT_PERIOD)

	const [loading, setLoading] = useState(false)
	const [trackableLinkData, setTrackableLinkData] = useState<TrackableLinkResponseData>({
		trackablelink: {} as Trackablelink,
		percentageOfConversationsTracked: 0
	})
	const [periodState, setPeriodState] = useState({
		dateRange: { fromDate: defaultFromDate, toDate: defaultToDate },
		period: DEFAULT_PERIOD as TrackableLinkInteractionsPeriod,
		previousPeriod: DEFAULT_PERIOD as TrackableLinkInteractionsPeriod
	})
	const [customPeriodState, setCustomPeriodState] = useState({
		fromDate: defaultFromDate as Date | null,
		toDate: defaultToDate as Date | null,
		error: "",
		isDialogOpen: false
	})
	const [hasResponded, setHasResponded] = useState<TrackableLinkInteractionsHasRespondedFilter>("")
	const [periodSummary, setPeriodSummary] = useState<TrackableLinkInteractionsSummary>(DEFAULT_PERIOD_SUMMARY)

	const classes = useStyles()

	const percentageOfConversationsTrackedInPeriod = periodSummary.totalClicked > 0
		? Math.round((periodSummary.totalResponded / periodSummary.totalClicked) * 100)
		: 0
	const redirectLinkToWhatsApp = mountTrackableLinkURL(trackableLinkData.trackablelink.hash, trackableLinkData.trackablelink.slug)
	const copyButtonText = `${inboxAppUrl.trackableLinkURL}/${trackableLinkData.trackablelink.hash}/${trackableLinkData.trackablelink.slug}`

	const copyHandler = (content: string) => {
		copy(content)
		Notification.success({ message: "Link copiado com sucesso!" })
	}

	const handlePeriodSelectChange = (event: React.ChangeEvent<{ value: unknown }>): void => {
		const newPeriod = event.target.value as TrackableLinkInteractionsPeriod

		if (newPeriod === "custom") {
			setPeriodState(prev => ({
				...prev,
				previousPeriod: prev.period,
				period: "custom"
			}))
			setCustomPeriodState(prev => ({ ...prev, isDialogOpen: true }))
			return
		}

		const newDateRange = calculateDateRangeForPeriod(newPeriod)
		setPeriodState(prev => ({
			...prev,
			period: newPeriod,
			dateRange: newDateRange
		}))
	}

	const handleHasRespondedSelectChange = (event: React.ChangeEvent<{ value: unknown }>): void => {
		setHasResponded(event.target.value as TrackableLinkInteractionsHasRespondedFilter)
	}

	const handleCustomPeriodApply = (): void => {
		if (!customPeriodState.fromDate || !customPeriodState.toDate) {
			setCustomPeriodState(prev => ({ ...prev, error: "Informe as datas de início e fim." }))
			return
		}

		const diffInDays = getDifferenceInDays(customPeriodState.toDate, customPeriodState.fromDate)

		if (diffInDays < 0) {
			setCustomPeriodState(prev => ({ ...prev, error: "A data de início não pode ser maior que a data final." }))
			return
		}

		if (diffInDays > MAX_CUSTOM_RANGE_IN_DAYS) {
			setCustomPeriodState(prev => ({ ...prev, error: `O período máximo é de ${MAX_CUSTOM_RANGE_IN_DAYS} dias.` }))
			return
		}

		setPeriodState(prev => ({
			...prev,
			dateRange: { fromDate: customPeriodState.fromDate, toDate: customPeriodState.toDate }
		}))
		setCustomPeriodState(prev => ({ ...prev, error: "", isDialogOpen: false }))
	}

	const handleCustomPeriodDialogClose = (): void => {
		setPeriodState(prev => ({
			...prev,
			period: prev.previousPeriod
		}))
		setCustomPeriodState(prev => ({ ...prev, error: "", isDialogOpen: false }))
	}

	const handleGetTrackableLinkById = async () => {
		setLoading(true)
		try {
			const { data } = await ApiService.get<TrackableLinkResponseData>(`/trackable-links/${trackableLinkId}`)
			setTrackableLinkData({
				trackablelink: data.trackablelink,
				percentageOfConversationsTracked: data.percentageOfConversationsTracked
			})
			setLoading(false)
		} catch (error) {
			setLoading(false)
			Notification.error({ message: "Ocorreu um erro ao buscar as métricas" })

			triggerValidation(error as ErrorType)
			ErrorHandlerService.handle(error as ErrorType)
		}
	}

	useDidMount(async () => {
		await handleGetTrackableLinkById()
	})

	return (
		<Grid
			container
			direction="column"
			spacing={5}
		>
			<Grid item>
				<Loading
					customLoadingElement={<TrackableLinkMetricsSkeleton />}
					loading={loading}
				>
					<Grid container direction="column"
						spacing={5}>
						<Grid item>
							<Breadcrumb
								data={[
									{ name: "Links rastreáveis", pathname: "/admin/conversion/tools/trackable-links" },
									{ name: `${trackableLinkData.trackablelink.title}`, pathname: `/admin/conversion/tools/trackable-links/${trackableLinkData.trackablelink.id}/details` }
								]}
							/>
						</Grid>

						<Grid item>
							<Grid container direction="column" spacing={1}>
								<Grid item>
									<Grid container justifyContent="space-between" alignItems="center" spacing={2}>
										<Grid item>
											<Typography
												variant="h5"
												className={classes.titleText}
											>
												{trackableLinkData.trackablelink.title}
											</Typography>
										</Grid>
										<Grid item>
											<ActionsDropdown
												copyLinkUrl={redirectLinkToWhatsApp}
												trackableLinkId={trackableLinkData.trackablelink.id}
												hiddenOptions={["details"]}
												fromDetailsPage
											/>
										</Grid>
									</Grid>
								</Grid>
								<Grid item>
									<Tooltip title="Copiar link">
										<Button
											variant="text"
											className={classes.copyButton}
											size="small"
											onClick={() => copyHandler(redirectLinkToWhatsApp)}
											startIcon={<LinkIcon fontSize="default"/>}
										>
											{copyButtonText}
										</Button>
									</Tooltip>
								</Grid>
							</Grid>
						</Grid>

						<Grid item>
							<Grid container alignItems="center" spacing={2}>
								<Grid item>
									<Typography className={classes.sectionTitle}>
										Filtros:
									</Typography>
								</Grid>
								<Grid item>
									<Select
										variant="outlined"
										value={periodState.period}
										onChange={handlePeriodSelectChange}
										className={classes.filterSelect}
										MenuProps={{
											getContentAnchorEl: null,
											anchorOrigin: { vertical: "bottom", horizontal: "left" },
											transformOrigin: { vertical: "top", horizontal: "left" }
										}}
									>
										{periodOptions.map((option) => (
											<MenuItem key={option.value} value={option.value}>
												{option.label}
											</MenuItem>
										))}
									</Select>
								</Grid>
								<Grid item>
									<Select
										variant="outlined"
										value={hasResponded}
										onChange={handleHasRespondedSelectChange}
										displayEmpty
										className={classes.filterSelect}
									>
										<MenuItem value="">Todos os cliques</MenuItem>
										<MenuItem value="responded">Enviaram mensagem</MenuItem>
										<MenuItem value="not-responded">Não enviaram mensagem</MenuItem>
									</Select>
								</Grid>
							</Grid>
						</Grid>

						<Grid item>
							<SetupTrackableLinkAccordion trackableLinkToSetup={trackableLinkData.trackablelink} />
						</Grid>

						<Grid item>
							<Grid container direction="column" spacing={1}>
								<Grid item>
									<Typography className={classes.sectionTitle}>
										Histórico de cliques e engajamento
									</Typography>
								</Grid>
								<Grid item>
									<MetricCards
										clicksCount={periodSummary.totalClicked}
										sentMessagesCount={periodSummary.totalResponded}
										percentageOfConversationsTracked={percentageOfConversationsTrackedInPeriod}
										allTimeClicksCount={trackableLinkData.trackablelink.consolidatedData?.clicksCount}
										allTimeSentMessagesCount={trackableLinkData.trackablelink.consolidatedData?.sentMessagesCount}
										allTimePercentageOfConversationsTracked={trackableLinkData.percentageOfConversationsTracked}
									/>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
				</Loading>
			</Grid>

			<Grid item>
				<Grid container direction="column" spacing={9}>
					<Grid item>
						<TrackableLinkInteractions
							fromDate={periodState.dateRange.fromDate}
							toDate={periodState.dateRange.toDate}
							hasResponded={hasResponded}
							onSummaryChange={setPeriodSummary}
						/>
					</Grid>
					<Grid item>
						<TrackableLinkUTMMetrics />
					</Grid>
				</Grid>
			</Grid>

			<ActionDialog
				title="Selecionar período personalizado"
				openDialog={customPeriodState.isDialogOpen}
				onSave={handleCustomPeriodApply}
				onClose={handleCustomPeriodDialogClose}
				saveText="Aplicar"
				cancelText="Cancelar"
				fullWidth
				maxWidth="sm"
			>
				<Grid container direction="column" spacing={3}>
					<Grid item xs={12}>
						<Typography>
							Selecione o período desejado para visualizar as métricas de cliques e respostas do link.
						</Typography>
					</Grid>
					<Grid item xs={12}>
						<Grid container spacing={2}>
							<Grid item xs={6}>
								<Grid container direction="column" spacing={1}>
									<Grid item>
										<Typography variant="subtitle1">
											Data de início:
										</Typography>
									</Grid>
									<Grid item>
										<MuiPickersUtilsProvider utils={DateFnsUtils} locale={ptBR}>
											<DateTimePicker
												ampm={false}
												format="dd/MM/yyyy HH:mm"
												animateYearScrolling
												views={["year", "month", "date", "hours", "minutes"]}
												variant="inline"
												inputVariant="outlined"
												fullWidth
												autoOk
												value={customPeriodState.fromDate}
												onChange={(date) => setCustomPeriodState((prev) => ({ ...prev, fromDate: date }))}
												maxDate={new Date()}
											/>
										</MuiPickersUtilsProvider>
									</Grid>
								</Grid>
							</Grid>
							<Grid item xs={6}>
								<Grid container direction="column" spacing={1}>
									<Grid item>
										<Typography variant="subtitle1">
											Data final:
										</Typography>
									</Grid>
									<Grid item>
										<MuiPickersUtilsProvider utils={DateFnsUtils} locale={ptBR}>
											<DateTimePicker
												ampm={false}
												format="dd/MM/yyyy HH:mm"
												animateYearScrolling
												views={["year", "month", "date", "hours", "minutes"]}
												variant="inline"
												inputVariant="outlined"
												fullWidth
												autoOk
												value={customPeriodState.toDate}
												onChange={(date) => setCustomPeriodState((prev) => ({ ...prev, toDate: date }))}
												minDate={customPeriodState.fromDate || undefined}
												maxDate={getCustomPeriodEndDateLimit(customPeriodState.fromDate)}
											/>
										</MuiPickersUtilsProvider>
									</Grid>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
					<Grid item xs={12}>
						{customPeriodState.error && (
							<Alert severity="error" icon={false}>
								{customPeriodState.error}
							</Alert>
						)}
						<Alert severity="warning" icon={false}>
							O filtro por data está disponível para um intervalo máximo de {MAX_CUSTOM_RANGE_IN_DAYS} dias.
						</Alert>
					</Grid>
				</Grid>
			</ActionDialog>
		</Grid>
	)
}

export default TrackableLinkMetrics
